const initFormTables = () => {
  // Find all table fields.
  const getFormTables = () =>
    <NodeListOf<HTMLElement>>document.querySelectorAll(".js-form-table");

  // Find fields which control a given table.
  const getTableControlFields = (tableHandle: string) =>
    <HTMLInputElement[]>[
      ...document.querySelectorAll(`[table-control="${tableHandle}"]`),
    ];

  // Find active fields (i.e. checked or value non-empty) which control a given table.
  const getTableActiveControlFields = (tableHandle: string) =>
    getTableControlFields(tableHandle).filter((input) =>
      input.type === "radio" || input.type === "checkbox"
        ? input.checked
        : input.value
    );

  // Find the add row button for a given table.
  const getTableAddRowButton = (tableHandle: string) =>
    <HTMLElement | null>(
      document.querySelector(
        `[data-freeform-table-add-row][data-target="form-input-${tableHandle}"]`
      )
    );

  // Find the table field wrapper element.
  const getTableByHandle = (tableHandle: string) =>
    document.querySelector(
      `[data-freeform-table][data-handle="${tableHandle}"]`
    );

  // Find all remove row buttons for a given table.
  const getTableRemoveRowButtons = (tableHandle: string) =>
    <NodeListOf<HTMLElement>>(
      document.querySelectorAll(
        `[data-freeform-table-remove-row][data-target="form-input-${tableHandle}"]`
      )
    );

  // Find the last remove row button for a given table.
  const getTableLastRemoveRowButton = (tableHandle: string) => {
    const response = getTableRemoveRowButtons(tableHandle);

    return response[response.length - 1];
  };

  // Find the number of rows in a given table.
  const getTableActualRowCount = (tableHandle: string) =>
    getTableByHandle(tableHandle)?.querySelectorAll("tbody > tr").length || 0;

  // Find the number of rows which should exist in a given table.
  const getTableDesiredRowCount = (tableHandle: string) => {
    return getTableActiveControlFields(tableHandle)
      .map(
        (field) =>
          (Number(field.value) || 0) *
          Number(field.getAttribute("quantity-multiplier") || 1)
      )
      .reduce((a, b) => a + b, 0);
  };

  // Make the number of rows match the desired number for a given table.
  const updateTableRowCount = (tableHandle: string) => {
    const desiredRowCount = getTableDesiredRowCount(tableHandle);
    const actualRowCount = getTableActualRowCount(tableHandle);

    // The desired and actual count already match.
    if (desiredRowCount === actualRowCount) {
      return;
    }

    // The desired count is greater than the actual count: add more rows.
    if (desiredRowCount > actualRowCount) {
      Array(desiredRowCount - actualRowCount)
        .fill(null)
        .forEach(() => getTableAddRowButton(tableHandle)?.click());
    }

    // The actual count is greater than the desired count: remove some rows.
    if (actualRowCount > desiredRowCount) {
      Array(actualRowCount - desiredRowCount)
        .fill(null)
        .forEach(() => getTableLastRemoveRowButton(tableHandle)?.click());
    }
  };

  // Initialize a given table field element.
  const initFormTable = (table: HTMLElement) => {
    // Skip if no control fields exist.
    if (
      !table.dataset.handle ||
      getTableControlFields(table.dataset.handle).length === 0
    ) {
      return;
    }

    // Hide the add and remove row buttons.
    getTableByHandle(table.dataset.handle)
      ?.closest(".js-field-wrapper")
      ?.classList.add("is-row-count-controlled");

    // Update the table when relevant control fields change.
    getTableControlFields(table.dataset.handle).forEach((controlField) => {
      controlField.addEventListener("change", () => {
        if (!table.dataset.handle) {
          return;
        }

        updateTableRowCount(table.dataset.handle);
      });
    });

    // Make sure the row count is accurate on page load.
    document.addEventListener("freeform-ready", () =>
      setTimeout(() => {
        if (!table.dataset.handle) {
          return;
        }

        updateTableRowCount(table.dataset.handle);
      }, 100)
    );
  };

  getFormTables().forEach(initFormTable);
};

export default initFormTables();
